import React, { 
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  useFlexLayout, useSortBy, useTable
} from 'react-table'

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { Checkbox } from '@primer/react';

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';

import ColumnSortIcon from './ColumnSortIcon';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import Label from '../Labels/Label';
import VerticalGradientOverlay from '../overlays/VerticalGradientOverlay';

const defaultPropGetter = () => ({});

const RowSelectionCell = ({ value, meta = {} }) => {
  const isChecked = meta?.isChecked(value) || false;
  const onCheckboxChange = meta?.onCheckboxChange;
  const hideCheckboxes = meta?.hideCheckboxes;

  const onChange = useCallback((e) => {
    onCheckboxChange(e, value, isChecked);
  }, [isChecked, onCheckboxChange, value]);

  return (
    <div className="flex p-2">
      {
        !hideCheckboxes && (
          <Checkbox
            id={`row-selection-checkbox-${value}`}
            checked={isChecked}
            onChange={onChange}
          />
        )
      }
    </div>
  )
}

const TableRow = ({ index, style, data }) => {
  const {
    rows,
    prepareRow,
    tableRef,
    rowHeights,
    onItemClick,
    getRowProps,
    getColumnProps,
    getCellProps,
  } = data;
  const rowRef = useRef({});
  const row = rows[index]
  prepareRow(row)

  const setRowHeight = useCallback((rowIndex, size) => {
    tableRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [tableRef, rowHeights]);

  const hasClickFunction = onItemClick && isFunction(onItemClick);
  const onClick = useCallback(() => {
    if (hasClickFunction) {
      onItemClick(row, index);
    }
  }, [hasClickFunction, row, index, onItemClick])

  const handleKeyPress = useCallback((e) => {
    const keyEvent = e.key;
    if (hasClickFunction && keyEvent.toLowerCase() === 'enter') {
      onItemClick(row, index)
    }
  }, [hasClickFunction, index, onItemClick, row])

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div
      ref={rowRef}
      onClick={onClick}
      onKeyDown={handleKeyPress}
      className={
        classNames(
          'w-full items-center hover:bg-border-light py-8 px-2',
          { 'bg-white': index % 2 === 0 },
          { 'bg-background': index % 2 !== 0 },
          { 'cursor-pointer active:bg-border-dark': hasClickFunction }
        )
      }
      role="row"
      tabIndex="0"
      {...row.getRowProps({
        ...getRowProps(row),
        style,
      })}
    >
      {row.cells.map(cell => (
        <div
          {...cell.getCellProps([
            {
              className: cell.column.className,
              style: cell.column.style,
            },
            getColumnProps(cell.column),
            getCellProps(cell),
          ])}
        >
          <Label>{cell.render('Cell')}</Label>
        </div>
      ))}
    </div>
  )
}

// TODO - Ensure Grouped Columns are displayed properly
const ColumnHeader = ({ 
  column, getHeaderProps, getColumnProps, meta = {}, centerColumns = false,
}) => {
  if (column?.isRowSelectionColumn) {
    const toggleAllRows = meta?.toggleAllRows;
    const isColumnChecked = meta?.isSelectionColumnChecked;
    const hideCheckboxes = meta?.hideCheckboxes;
    return (
      <th
        {...column.getHeaderProps([
          {
            className: column.className,
            style: column.style,
          },
          getColumnProps(column),
          getHeaderProps(column),
          column.getSortByToggleProps(),
        ])}
      >
        <div className="flex justify-start items-center p-2">
          {
            !hideCheckboxes && (
              <Checkbox
                id="row-selection-checkbox-all"
                checked={isColumnChecked}
                onChange={toggleAllRows}
              />
            )
          }
        </div>
      </th>
    )
  }

  return (
    <th
      className={classNames(
        "flex items-center text-brand hover:text-border-darkest active:text-border-dark",
        { 'justify-center': centerColumns }
      )}
      {...column.getHeaderProps([
        {
          className: column.className,
          style: column.style,
        },
        getColumnProps(column),
        getHeaderProps(column),
        column.getSortByToggleProps(),
      ])}
    >
      {column.render('Header')}
      {
        column?.tooltipText && (
          <InfoTooltip
            className="fill-brand ml-2"
            align="right"
            direction="nw"
            label={column.tooltipText}
          />
        )
      }
      {
        !column.disableSortBy && (
          <ColumnSortIcon
            isSorted={column.isSorted}
            isSortedDesc={column.isSortedDesc}
          />
        )
      }
    </th>
  )
}

// TODO - This logic can be refactored into the DataTable component
// This did not happen initially as a result of delivery time for Orchestration

// Sorting, virtualization, and data driven rendering
export const AlternatingRowDataTable = ({
  id,
  className,
  columns,
  data,
  defaultColumn,
  onItemClick,
  defaultSort = null,
  selectedRowIds = [],
  pagination = {},
  selectableRowProp = 'id',
  useSelectableRows = false,
  hideCheckboxes = false,
  hideScrollFading = false,
  isSelectionColumnChecked = false,
  setSelectionColumnChecked = () => {},
  height = 720,
  estimatedItemSize = 96,
  minItemSize = 96,
  meta = undefined,
  getHeaderProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
  setSelectedRowIds = () => {},
}) => {
  const tableRef = useRef({});
  const rowHeights = useRef({});

  const columnsToDisplay = useMemo(() => {
    if (!useSelectableRows) {
      return columns;
    }
    return [
      {
        Header: '',
        accessor: selectableRowProp,
        Cell: RowSelectionCell,
        width: 12,
        isRowSelectionColumn: true,
        disableSortBy: true,
      },
      ...columns,
    ]
  }, [columns, selectableRowProp, useSelectableRows]);
  
  const isChecked = useCallback((itemId) => {
    if (!selectedRowIds?.length) {
      return false;
    }
    return selectedRowIds.includes(itemId);
  }, [selectedRowIds]);

  const onCheckboxChange = useCallback((e, itemId, selectedToRemove = false) => {
    if (selectedToRemove) {
      const indexToRemove = selectedRowIds.indexOf(itemId);
      const newSelections = [...selectedRowIds];
      newSelections.splice(indexToRemove, 1)

      setSelectedRowIds(newSelections)
    } else {
      setSelectedRowIds([
        ...selectedRowIds,
        itemId,
      ])
    }
    e.stopPropagation();
  }, [selectedRowIds, setSelectedRowIds])

  const toggleAllRows = useCallback(() => {
    if (isSelectionColumnChecked) {
      setSelectedRowIds([]);
    } else {
      const rowIds = data.map(item => item[selectableRowProp]);
      setSelectedRowIds(rowIds);
    }
    setSelectionColumnChecked(!isSelectionColumnChecked);
  }, [data, isSelectionColumnChecked, selectableRowProp, setSelectedRowIds, setSelectionColumnChecked])
  
  const metaProps = isObject(meta) ? {
    ...meta, isSelectionColumnChecked, isChecked, hideCheckboxes, onCheckboxChange, toggleAllRows
  } : {
    isChecked, isSelectionColumnChecked, hideCheckboxes, onCheckboxChange, toggleAllRows
  };

  const initialState = defaultSort ? {
    sortBy: [
      defaultSort
    ],
  } : null;

  const tableConfig = {
    columns: columnsToDisplay,
    data,
    defaultColumn,
    meta: metaProps,
    autoResetSortBy: false,
  };

  if (initialState) {
    tableConfig.initialState = initialState;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    tableConfig,
    useSortBy,
    useFlexLayout,
  )

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < minItemSize) {
      return minItemSize;
    }
    return rowHeight || estimatedItemSize;
  }, [estimatedItemSize, minItemSize, rowHeights]);

  const tableDisplayProps = { };
  if (className) {
    tableDisplayProps.className = className;
  }

  const rowsToDisplay = useMemo(() => {
    const resultsPerPage = pagination?.resultsPerPage || 25;
    const selectedPage = pagination?.selectedPage || 1;
    if (!pagination || rows.length <= resultsPerPage) {
      return rows;
    }
    const pageStartIndex = (selectedPage - 1) * resultsPerPage;
    const pageEndIndex = pageStartIndex + resultsPerPage;
    return rows.slice(pageStartIndex, pageEndIndex);
  }, [rows, pagination]);

  return (
    <table id={id} className="h-full w-full overflow-x-scroll" {...getTableProps()}>
      {headerGroups.map(headerGroup => (
        <thead className="relative w-full p-2 border-b border-border-dark shadow bg-background" {...headerGroup.getHeaderGroupProps()} style={{ display: 'flex' }}>
          <tr className="w-full flex justify-start">
            {
              headerGroup.headers.map(column => (
                <ColumnHeader
                  key={column?.id || column?.Header}
                  column={column}
                  meta={metaProps}
                  getColumnProps={getColumnProps}
                  getHeaderProps={getHeaderProps}
                  centerColumns={column?.centerColumn}
                />
              ))
            }
          </tr>
        </thead>
      ))}
      <tbody className="bg-white w-full relative" {...getTableBodyProps()}>
      {
        !hideScrollFading && (
          <VerticalGradientOverlay as="tr" className="top-0 left-0 right-0 mx-0.5" fromDirection="b" />
        )
      }
        <tr>
          <td>
            <AutoSizer disableHeight>
              {
                ({ width }) => (
                  <VariableSizeList
                    ref={tableRef}
                    height={height}
                    itemCount={rowsToDisplay.length}
                    estimatedItemSize={estimatedItemSize}
                    itemSize={getRowHeight}
                    width={width}
                    itemData={{
                      rows: rowsToDisplay,
                      prepareRow,
                      tableRef,
                      rowHeights,
                      onItemClick,
                      getRowProps,
                      getColumnProps,
                      getCellProps,
                    }}
                  >
                    {TableRow}
                  </VariableSizeList>
                )
              }
            </AutoSizer>
          </td>
        </tr>
      {
        !hideScrollFading && (
          <VerticalGradientOverlay as="tr" className="bottom-0 left-0 right-0 mx-0.5" fromDirection="t" />
        )
      }
      </tbody>
    </table>
  )
}

export default AlternatingRowDataTable;
